import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { getPortalRoot } from './portalRoot';

/** Delay before showing, so sweeping the cursor across a table row does not flash tooltips. */
const OPEN_DELAY_MS = 150;
const TRIGGER_GAP_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

interface TooltipProps {
    children: ReactNode;
    /** Tooltip body. Plain strings render with preserved line breaks. */
    content: ReactNode;
    /** Extra classes for the inline trigger wrapper. */
    className?: string;
}

/**
 * Instant styled hover tooltip, replacing the native `title` attribute where its ~1s hover delay
 * and unstyled rendering make detail too easy to miss. Rendered through the shared portal root so
 * it escapes overflow-clipped containers such as table cells; positioned below the trigger and
 * flipped above when the viewport bottom is too close.
 */
const Tooltip = ({ children, content, className }: TooltipProps) => {
    /**
     * Resolved on first open rather than at mount: the portal root lives on `document`, which does
     * not exist when this renders on the server (component tests render via react-dom/server).
     * Stable for the rest of the instance's lifetime, as the portal-root contract requires.
     */
    const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const bubbleRef = useRef<HTMLDivElement>(null);
    const openTimeoutRef = useRef<number | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [bubblePosition, setBubblePosition] = useState<{ left: number; top: number } | null>(null);

    const cancelPendingOpen = useCallback(() => {
        if (openTimeoutRef.current === null) return;

        window.clearTimeout(openTimeoutRef.current);
        openTimeoutRef.current = null;
    }, []);

    const open = useCallback(() => {
        cancelPendingOpen();
        openTimeoutRef.current = window.setTimeout(() => {
            openTimeoutRef.current = null;
            setPortalRoot((currentRoot) => currentRoot ?? getPortalRoot());
            setIsOpen(true);
        }, OPEN_DELAY_MS);
    }, [cancelPendingOpen]);

    const close = useCallback(() => {
        cancelPendingOpen();
        setIsOpen(false);
        setBubblePosition(null);
    }, [cancelPendingOpen]);

    useEffect(() => cancelPendingOpen, [cancelPendingOpen]);

    // The bubble renders hidden first so its size can be measured, then is placed and revealed.
    useLayoutEffect(() => {
        if (!isOpen) return;

        const trigger = triggerRef.current;
        const bubble = bubbleRef.current;

        if (!trigger || !bubble) throw new Error('Tooltip trigger or bubble not mounted');

        const triggerRect = trigger.getBoundingClientRect();
        const bubbleRect = bubble.getBoundingClientRect();
        const left = Math.min(
            Math.max(VIEWPORT_MARGIN_PX, triggerRect.left + triggerRect.width / 2 - bubbleRect.width / 2),
            window.innerWidth - bubbleRect.width - VIEWPORT_MARGIN_PX
        );
        const fitsBelow = triggerRect.bottom + TRIGGER_GAP_PX + bubbleRect.height <= window.innerHeight - VIEWPORT_MARGIN_PX;

        setBubblePosition({
            left,
            top: fitsBelow ? triggerRect.bottom + TRIGGER_GAP_PX : triggerRect.top - TRIGGER_GAP_PX - bubbleRect.height
        });
    }, [isOpen]);

    // The fixed-position bubble does not track its trigger, so any scroll closes it.
    useEffect(() => {
        if (!isOpen) return;

        window.addEventListener('scroll', close, true);
        return () => window.removeEventListener('scroll', close, true);
    }, [close, isOpen]);

    const bubbleStyle: CSSProperties = bubblePosition ?? { left: 0, top: 0, visibility: 'hidden' };

    return (
        <span
            className={`inline-flex min-w-0 ${className ?? ''}`}
            onBlur={close}
            onFocus={open}
            onMouseEnter={open}
            onMouseLeave={close}
            ref={triggerRef}
        >
            {children}
            {isOpen &&
                portalRoot &&
                createPortal(
                    <div
                        className="pointer-events-none fixed z-50 max-w-sm rounded-md border border-zinc-700 bg-zinc-900 px-3 py-2 text-xs leading-5 whitespace-pre-line text-zinc-200 shadow-lg"
                        ref={bubbleRef}
                        role="tooltip"
                        style={bubbleStyle}
                    >
                        {content}
                    </div>,
                    portalRoot
                )}
        </span>
    );
};

export default Tooltip;
